import { createFileRoute } from '@tanstack/react-router'
import { Plus, Wallet, Building2, Smartphone, Ban } from 'lucide-react'
import { useState } from 'react'

import { AccountForm } from '@/components/finance/account/account-form'
import {
  useAccounts,
  useDeactivateAccount,
  type AccountWithBalance,
  type AccountType,
} from '@/hooks'
import { useConfirmDialog , useCurrencyFormatter } from '@/hooks'

export const Route = createFileRoute('/_authenticated/finance/accounts/' as never)({
  component: AccountListPage,
})



const accountTypeLabels: Record<AccountType, string> = {
  cash: '现金',
  bank: '银行',
  digital: '数字账户',
}

const accountTypeIcons: Record<AccountType, typeof Wallet> = {
  cash: Wallet,
  bank: Building2,
  digital: Smartphone,
}

function AccountListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingAccount, setEditingAccount] = useState<AccountWithBalance | null>(null)

  const { data: accounts, isLoading, error } = useAccounts()
  const deactivateMutation = useDeactivateAccount()
  const { formatMoney } = useCurrencyFormatter()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleDeactivate = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'deactivate',
      '确定要停用此账户吗？停用后将无法用于新的现金流记录。'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deactivateMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const activeAccounts = accounts?.filter((a) => a.isActive) ?? []
  const inactiveAccounts = accounts?.filter((a) => !a.isActive) ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">账户管理</h1>
          <p className="text-muted-foreground">管理公司财务账户</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          新建账户
        </button>
      </div>

      {(showCreateForm || editingAccount) && (
        <AccountForm
          account={editingAccount ?? undefined}
          onClose={() => {
            setShowCreateForm(false)
            setEditingAccount(null)
          }}
          onSuccess={() => {
            setShowCreateForm(false)
            setEditingAccount(null)
          }}
        />
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : activeAccounts.length === 0 && inactiveAccounts.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <Wallet className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无账户</p>
            <p className="text-sm text-muted-foreground">点击上方按钮创建第一个账户</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {activeAccounts.map((account) => {
              const Icon = accountTypeIcons[account.type]
              return (
                <div
                  key={account.id}
                  className="rounded-lg border bg-card p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{account.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {accountTypeLabels[account.type]}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditingAccount(account)}
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => void handleDeactivate(account.id)}
                        className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        停用
                      </button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">当前余额</p>
                    <p className="text-2xl font-bold">
                      {formatMoney(account.currentBalance, account.currency)}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          {inactiveAccounts.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-4 text-lg font-semibold text-muted-foreground">已停用账户</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {inactiveAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="rounded-lg border bg-muted/30 p-6 opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-muted p-2">
                        <Ban className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{account.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {accountTypeLabels[account.type]} · 已停用
                        </p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground">最终余额</p>
                      <p className="text-xl font-medium">
                        {formatMoney(account.currentBalance, account.currency)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog />
    </div>
  )
}
